import { HugeiconsIcon, type IconSvgElement } from '@hugeicons/react';
import {
  Add01Icon as hAdd01,
  Alert01Icon as hAlert01,
  Alert02Icon as hAlert02,
  AlertCircleIcon as hAlertCircle,
  ArrowDataTransferHorizontalIcon as hArrowDataTransferHorizontal,
  ArrowDown01Icon as hArrowDown01,
  ArrowLeft01Icon as hArrowLeft01,
  ArrowLeft02Icon as hArrowLeft02,
  ArrowLeftDoubleIcon as hArrowLeftDouble,
  ArrowReloadHorizontalIcon as hArrowReloadHorizontal,
  ArrowRight01Icon as hArrowRight01,
  ArrowUp01Icon as hArrowUp01,
  Calendar03Icon as hCalendar03,
  Call02Icon as hCall02,
  CancelCircleIcon as hCancelCircle,
  ChartDecreaseIcon as hChartDecrease,
  CheckmarkCircle02Icon as hCheckmarkCircle02,
  Clock01Icon as hClock01,
  CloudDownloadIcon as hCloudDownload,
  CloudSavingDone01Icon as hCloudSavingDone01,
  CloudServerIcon as hCloudServer,
  CloudUploadIcon as hCloudUpload,
  ComputerIcon as hComputer,
  Copy01Icon as hCopy01,
  CreditCardIcon as hCreditCard,
  Database01Icon as hDatabase01,
  Delete02Icon as hDelete02,
  Eraser01Icon as hEraser01,
  FileImportIcon as hFileImport,
  FlashIcon as hFlash,
  Home01Icon as hHome01,
  InformationCircleIcon as hInformationCircle,
  Invoice01Icon as hInvoice01,
  KeyboardIcon as hKeyboard,
  Loading03Icon as hLoading03,
  Location01Icon as hLocation01,
  LockIcon as hLock,
  Logout03Icon as hLogout03,
  Mail01Icon as hMail01,
  Medicine02Icon as hMedicine02,
  Menu01Icon as hMenu01,
  Moon02Icon as hMoon02,
  MoreVerticalIcon as hMoreVertical,
  Notification01Icon as hNotification01,
  PackageIcon as hPackage,
  PackageDeliveredIcon as hPackageDelivered,
  PackageOpenIcon as hPackageOpen,
  PackageProcessIcon as hPackageProcess,
  PrescriptionIcon as hPrescription,
  PrinterIcon as hPrinter,
  Refresh01Icon as hRefresh01,
  Search01Icon as hSearch01,
  SecurityCheckIcon as hSecurityCheck,
  Settings01Icon as hSettings01,
  ShoppingBag01Icon as hShoppingBag01,
  ShoppingCart01Icon as hShoppingCart01,
  SidebarLeft01Icon as hSidebarLeft01,
  SidebarLeftIcon as hSidebarLeft,
  SlidersHorizontalIcon as hSlidersHorizontal,
  SmartphoneIcon as hSmartphone,
  Store01Icon as hStore01,
  Sun03Icon as hSun03,
  Task01Icon as hTask01,
  Tick02Icon as hTick02,
  UnavailableIcon as hUnavailable,
  UnfoldMoreIcon as hUnfoldMore,
  UserIcon as hUser,
  Wallet01Icon as hWallet01,
  WifiDisconnected01Icon as hWifiDisconnected01,
  Wrench01Icon as hWrench01,
} from '@hugeicons/core-free-icons';
import type { SVGProps } from 'react';

/**
 * Every icon in the app, by what it MEANS here.
 *
 * Pages import `RefreshIcon` or `RxIcon`, never a Hugeicons glyph. The glyph
 * is decided once, below, so changing what "refresh" looks like is one line
 * here rather than a hunt across forty files - and two screens cannot pick
 * two different arrows for the same action. A new icon is added to the
 * registry first and imported from here; nothing imports
 * `@hugeicons/core-free-icons` directly.
 *
 * The glyphs are Hugeicons Stroke Rounded (the free set), imported by name so
 * the build keeps only these. They are aliased with an `h` prefix because
 * the app's names and Hugeicons' own often coincide (both have a
 * `PrescriptionIcon`), and an alias makes the two impossible to confuse.
 */

/**
 * The size scale. `1em` is the default: an icon follows the type it sits in,
 * the way the Ant icons it replaced did, so it drops into a button, an input
 * prefix or a line of text at the right size with no prop at all. The named
 * sizes are for an icon that stands on its own.
 */
const SIZES = { xs: 12, sm: 14, md: 16, lg: 20, xl: 24 } as const;
export type IconSize = keyof typeof SIZES | number | '1em';

export type IconProps = Omit<SVGProps<SVGSVGElement>, 'ref'> & {
  size?: IconSize;
  /**
   * In screen pixels at every size (see `.app-icon` in styles.css): 1.5 is a
   * 1.5px line whether the icon is 12px or 24px.
   */
  strokeWidth?: number;
  /** Turns continuously - for loading and syncing. */
  spin?: boolean;
  /**
   * Names the icon for a screen reader. Without it the icon is decorative and
   * hidden from assistive tech, which is right whenever text beside it (a
   * button label, a nav label) already says what it means.
   */
  label?: string;
};

/** One icon, drawn the app's way. Prefer the named components below. */
export function Icon({
  glyph,
  size = '1em',
  strokeWidth = 1.5,
  spin,
  label,
  className,
  ...rest
}: IconProps & { glyph: IconSvgElement }) {
  return (
    <HugeiconsIcon
      icon={glyph}
      size={typeof size === 'string' && size in SIZES ? SIZES[size as keyof typeof SIZES] : size}
      strokeWidth={strokeWidth}
      color="currentColor"
      className={['app-icon', spin ? 'app-icon-spin' : '', className ?? ''].filter(Boolean).join(' ')}
      focusable="false"
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
      {...rest}
    />
  );
}

/** A named icon component for the registry. */
function define(glyph: IconSvgElement, name: string, defaults?: Partial<IconProps>) {
  const Component = (props: IconProps) => <Icon glyph={glyph} {...defaults} {...props} />;
  Component.displayName = name;
  return Component;
}

// ── actions ─────────────────────────────────────────────────────────────────
export const AddIcon = define(hAdd01, 'AddIcon');
export const RefreshIcon = define(hRefresh01, 'RefreshIcon');
export const DeleteIcon = define(hDelete02, 'DeleteIcon');
export const SearchIcon = define(hSearch01, 'SearchIcon');
export const PrintIcon = define(hPrinter, 'PrintIcon');
export const ImportIcon = define(hFileImport, 'ImportIcon');
export const DuplicateIcon = define(hCopy01, 'DuplicateIcon');
export const ClearIcon = define(hEraser01, 'ClearIcon');
export const UploadIcon = define(hCloudUpload, 'UploadIcon');
export const DownloadIcon = define(hCloudDownload, 'DownloadIcon');
export const SignOutIcon = define(hLogout03, 'SignOutIcon');
export const MoreIcon = define(hMoreVertical, 'MoreIcon');
export const CheckIcon = define(hTick02, 'CheckIcon');
export const SwapIcon = define(hArrowDataTransferHorizontal, 'SwapIcon');

// ── moving around ───────────────────────────────────────────────────────────
export const BackIcon = define(hArrowLeft02, 'BackIcon');
export const ChevronUpIcon = define(hArrowUp01, 'ChevronUpIcon');
export const ChevronDownIcon = define(hArrowDown01, 'ChevronDownIcon');
export const ChevronLeftIcon = define(hArrowLeft01, 'ChevronLeftIcon');
export const ChevronRightIcon = define(hArrowRight01, 'ChevronRightIcon');
/** Opens a menu that can go up or down - the account row. */
export const ChevronUpDownIcon = define(hUnfoldMore, 'ChevronUpDownIcon');
export const FirstPageIcon = define(hArrowLeftDouble, 'FirstPageIcon');
export const MenuIcon = define(hMenu01, 'MenuIcon');
export const CollapseSidebarIcon = define(hSidebarLeft01, 'CollapseSidebarIcon');
export const ExpandSidebarIcon = define(hSidebarLeft, 'ExpandSidebarIcon');

// ── the app's places ────────────────────────────────────────────────────────
export const HomeIcon = define(hHome01, 'HomeIcon');
export const OrdersIcon = define(hShoppingCart01, 'OrdersIcon');
export const RxIcon = define(hPrescription, 'RxIcon');
export const SaleIcon = define(hShoppingBag01, 'SaleIcon');
export const RegisterIcon = define(hInvoice01, 'RegisterIcon');
export const HistoryIcon = define(hTask01, 'HistoryIcon');
export const StoreIcon = define(hStore01, 'StoreIcon');
export const StockIcon = define(hPackage, 'StockIcon');
export const BatchIcon = define(hPackageOpen, 'BatchIcon');
export const ExpiryIcon = define(hAlert01, 'ExpiryIcon');
export const LowStockIcon = define(hChartDecrease, 'LowStockIcon');
export const ClockIcon = define(hClock01, 'ClockIcon');
export const SettingsIcon = define(hSettings01, 'SettingsIcon');
export const DeviceIcon = define(hComputer, 'DeviceIcon');
export const OfflineDataIcon = define(hCloudServer, 'OfflineDataIcon');
export const PreferencesIcon = define(hSlidersHorizontal, 'PreferencesIcon');
export const ShortcutsIcon = define(hKeyboard, 'ShortcutsIcon');
export const TroubleshootIcon = define(hWrench01, 'TroubleshootIcon');
export const StorageIcon = define(hDatabase01, 'StorageIcon');
export const CloudSyncIcon = define(hCloudSavingDone01, 'CloudSyncIcon');
export const BellIcon = define(hNotification01, 'BellIcon');
/** An order's bag, packed and ready to collect. */
export const BagReadyIcon = define(hPackageDelivered, 'BagReadyIcon');
/** An order being collected off the shelf. */
export const PickingIcon = define(hPackageProcess, 'PickingIcon');

// ── people and contact ──────────────────────────────────────────────────────
export const PersonIcon = define(hUser, 'PersonIcon');
export const PhoneIcon = define(hCall02, 'PhoneIcon');
export const MobileIcon = define(hSmartphone, 'MobileIcon');
export const MailIcon = define(hMail01, 'MailIcon');
export const LocationIcon = define(hLocation01, 'LocationIcon');
/** "Ref by": the prescriber. */
export const DoctorIcon = define(hMedicine02, 'DoctorIcon');

// ── money and dates ─────────────────────────────────────────────────────────
export const WalletIcon = define(hWallet01, 'WalletIcon');
export const CardIcon = define(hCreditCard, 'CardIcon');
export const CalendarIcon = define(hCalendar03, 'CalendarIcon');

// ── status ──────────────────────────────────────────────────────────────────
/** Loading. Spins by default, as the icon it replaced did. */
export const SpinnerIcon = define(hLoading03, 'SpinnerIcon', { spin: true });
export const SyncIcon = define(hArrowReloadHorizontal, 'SyncIcon');
export const SuccessIcon = define(hCheckmarkCircle02, 'SuccessIcon');
export const WarningIcon = define(hAlert02, 'WarningIcon');
export const NoticeIcon = define(hAlertCircle, 'NoticeIcon');
export const CloseCircleIcon = define(hCancelCircle, 'CloseCircleIcon');
export const InfoIcon = define(hInformationCircle, 'InfoIcon');
export const BlockedIcon = define(hUnavailable, 'BlockedIcon');
export const LockIcon = define(hLock, 'LockIcon');
export const VerifiedIcon = define(hSecurityCheck, 'VerifiedIcon');
export const OfflineIcon = define(hWifiDisconnected01, 'OfflineIcon');
export const BoltIcon = define(hFlash, 'BoltIcon');
export const LightModeIcon = define(hSun03, 'LightModeIcon');
export const DarkModeIcon = define(hMoon02, 'DarkModeIcon');
